import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/auth';
import { useGuardianPaths } from '../../../hooks/useGuardianPaths';
import { Button, Card, CardContent } from '@/components/ui';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { useAcademies, useBookings, useLessons, useTerms } from '../../../hooks/useDataStore';
import { useUserScenario } from '../../../hooks/useUserScenario';
import { TourProvider, useTour, TourAlertDialog } from '@/components/ui/Tour';
import { studentTourSteps } from '@/lib/tour-steps';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { PageHeader } from '@/components/layout/PageHeader';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { ClubMemberDashboardSection } from '@/components/member/ClubMemberDashboardSection';
import { useOrgMemberStore } from '@/store/orgMember';
import { Calendar, Check, UserPlus } from 'lucide-react';
import { waitlistService } from '../../../services/waitlist';
import type { WaitlistEntry } from '@court-sync/shared';
import toast from 'react-hot-toast';
import { formatTimeHHMM } from '@/utils/timeFormat';
import { getBookingEnrollmentScopeLabel } from '@/utils/bookingScopeLabel';
import { userService } from '../../../services/user';
import { useGuardianAcademyStore } from '../../../store/guardianAcademy';
import { useAcademySports } from '@/hooks/useAcademySports';
import { SportBadge } from '@/components/sport/SportBadge';
import { resolveAcademySport } from '@/lib/sportPresentation';

/**
 * Unified consumer portal home: lessons and/or club member sections.
 */
function StudentDashboardContent() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const consumerNav = useUnifiedConsumerNav();
  const hasLessonsAccess = consumerNav.useUnifiedNav
    ? consumerNav.hasLessonsAccess
    : true;
  const hasClubMemberAccess = consumerNav.useUnifiedNav && consumerNav.hasClubMemberAccess;
  const orgProfile = useOrgMemberStore((s) => s.orgProfile);
  const { scenario, students, selfStudent, academyId: scenarioAcademyId } = useUserScenario();

  const { bookings, isLoading: bookingsLoading } = useBookings(
    hasLessonsAccess && user?.id
      ? { customerId: user.id, academyId: scenarioAcademyId ?? user?.academyId }
      : undefined
  );
  const academyIdForData =
    scenarioAcademyId ?? user?.academyId ?? bookings[0]?.academyId;
  const { lessons } = useLessons(hasLessonsAccess ? academyIdForData : undefined);
  const { terms } = useTerms(hasLessonsAccess ? academyIdForData : undefined);

  const { academies, currentAcademy, isLoading: academiesLoading, loadAcademy } = useAcademies();

  const { setSteps, isTourCompleted } = useTour();
  const [showTourDialog, setShowTourDialog] = useState(false);
  const guardianAcademy = useGuardianAcademyStore((s) => s.academy);

  useEffect(() => {
    setSteps(studentTourSteps);
  }, [setSteps]);

  useEffect(() => {
    if (isTourCompleted) return undefined;
    const timer = window.setTimeout(() => setShowTourDialog(true), 500);
    return () => window.clearTimeout(timer);
  }, [isTourCompleted]);

  const userAcademy = currentAcademy || academies.find(a => a.id === (scenarioAcademyId ?? user?.academyId)) || null;
  const { sports: academySports, isMultiSport } = useAcademySports(guardianAcademy ?? userAcademy);

  // Load current academy from guardian context or user
  useEffect(() => {
    if (!hasLessonsAccess) return undefined;
    const aid = scenarioAcademyId ?? user?.academyId;
    if (aid && !currentAcademy) {
      loadAcademy(aid);
    }
  }, [hasLessonsAccess, scenarioAcademyId, user?.academyId, currentAcademy, loadAcademy]);

  // Load academy from bookings if userAcademy is not available but we have bookings
  useEffect(() => {
    if (!hasLessonsAccess) return undefined;
    if (!userAcademy && bookings.length > 0 && !academiesLoading) {
      const bookingAcademyId = bookings[0].academyId;
      const bookingAcademy = academies.find(a => a.id === bookingAcademyId);
      if (!bookingAcademy && bookingAcademyId) {
        loadAcademy(bookingAcademyId);
      }
    }
  }, [userAcademy, bookings, academies, academiesLoading, loadAcademy]);

  // Dashboard structure focused on enrolled lessons and academy info
  // Navigation is now in the sidebar, so we focus on student-specific content

  // Enrolled bookings: confirmed or pending (active enrollments)
  const enrolledBookings = bookings.filter(
    (booking) => booking.status === 'confirmed' || booking.status === 'pending'
  );

  const completedBookings = bookings.filter(
    (booking) => booking.status === 'completed'
  );

  const studentStats = {
    enrolledLessons: enrolledBookings.length,
    completedLessons: completedBookings.length,
  };

  const DAYS_OF_WEEK = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const formatLessonSchedule = (
    lesson: (typeof lessons)[0],
    booking: (typeof bookings)[0]
  ) => {
    if (booking.sessionDate) {
      return new Date(booking.sessionDate).toLocaleDateString();
    }
    if (lesson?.daysOfWeek?.length && lesson?.startTime && lesson?.endTime) {
      const dayNames = lesson.daysOfWeek
        .map((d) => DAYS_OF_WEEK[d])
        .join(', ');
      return `${dayNames} ${formatTimeHHMM(lesson.startTime)}–${formatTimeHHMM(lesson.endTime)}`;
    }
    return null;
  };

  // Get academy from bookings if userAcademy is not available
  const getAcademyFromBookings = () => {
    if (userAcademy) return userAcademy;
    if (bookings.length > 0) {
      const bookingAcademyId = bookings[0].academyId;
      const bookingAcademy = academies.find(a => a.id === bookingAcademyId);
      if (bookingAcademy) return bookingAcademy;
    }
    return null;
  };

  const displayAcademy = getAcademyFromBookings();
  const academyName =
    guardianAcademy?.name ?? displayAcademy?.name ?? userAcademy?.name ?? orgProfile?.organization.name ?? 'your academy';

  // Waitlist entries (guardian view)
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [leavingEntryId, setLeavingEntryId] = useState<string | null>(null);

  useEffect(() => {
    if (!hasLessonsAccess || !academyIdForData) return;
    setWaitlistLoading(true);
    waitlistService
      .getMyWaitlistEntries(academyIdForData)
      .then((res) => setWaitlistEntries(res.data || []))
      .catch(() => setWaitlistEntries([]))
      .finally(() => setWaitlistLoading(false));
  }, [academyIdForData]);

  const handleLeaveWaitlist = async (entryId: string) => {
    try {
      setLeavingEntryId(entryId);
      await waitlistService.leaveWaitlist(entryId);
      setWaitlistEntries((prev) => prev.filter((e) => e.id !== entryId));
      toast.success('Left waitlist');
    } catch {
      toast.error('Failed to leave waitlist');
    } finally {
      setLeavingEntryId(null);
    }
  };

  if (!user) {
    return null;
  }

  if (scenario === 'loading' && hasLessonsAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const dashboardTitle = hasLessonsAccess
    ? 'Student Dashboard'
    : hasClubMemberAccess
      ? 'Member Dashboard'
      : 'My Dashboard';

  return (
    <div className="min-h-screen">
      <div className="contents">
        <PageHeader
          title={dashboardTitle}
          help={
            <p>
              {hasLessonsAccess
                ? 'Welcome to your student dashboard'
                : 'Manage your club membership, bookings, and notices'}
            </p>
          }
        />

        {hasClubMemberAccess ? <ClubMemberDashboardSection /> : null}

        {hasLessonsAccess ? (
          <>
        {displayAcademy?.privateBookingEnabled && paths?.bookPrivate && (
          <Card className="mb-8 border-primary/30 bg-primary/5">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center shrink-0">
                  <UserPlus className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Book a private lesson</p>
                  <p className="text-sm text-muted-foreground">
                    Choose a coach and recurring time — pricing is based on your coach&apos;s hourly rate.
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate(paths.bookPrivate)} className="shrink-0">
                Start booking
              </Button>
            </CardContent>
          </Card>
        )}
        <div id={TOUR_STEP_IDS.STUDENT_STATS} className="mb-6 mt-6 grid grid-cols-2 gap-2 sm:gap-4">
          <DashboardStatCard
            icon={<Calendar className="text-primary-600 dark:text-primary-400" aria-hidden />}
            iconBg="bg-primary-100 dark:bg-primary-900"
            iconColor="text-primary-600 dark:text-primary-400"
            label="Enrolled Lessons"
            mobileLabel="Enrolled"
            value={studentStats.enrolledLessons}
          />
          <DashboardStatCard
            icon={<Check className="text-tennis-600 dark:text-tennis-400" aria-hidden />}
            iconBg="bg-tennis-100 dark:bg-tennis-900"
            iconColor="text-tennis-600 dark:text-tennis-400"
            label="Lessons Completed"
            mobileLabel="Completed"
            value={studentStats.completedLessons}
          />
        </div>

        {/* Enrolled Lessons and Academy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Enrolled Lessons
              </h3>
              <Button size="sm" variant="outline" onClick={() => navigate(paths?.bookings ?? '/org-picker')}>
                View All
              </Button>
            </div>
            <Card>
              <CardContent className="p-6">
                {bookingsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : enrolledBookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <div className="flex justify-center mb-2"><Calendar className="w-10 h-10 text-gray-400" /></div>
                    <p>No enrolled lessons</p>
                    <p className="text-sm mb-4">Book your first lesson to get started!</p>
                    <Button onClick={() => navigate(paths?.lessons ?? '/org-picker')}>
                      Browse Lessons
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {enrolledBookings.slice(0, 5).map((booking) => {
                      const lesson = lessons.find((l) => l.id === booking.lessonId);
                      const term = terms.find((t) => t.id === booking.termId);
                      const bookingStudent =
                        students.find((s) => s.id === booking.studentId) ||
                        (selfStudent && booking.studentId === selfStudent.id ? selfStudent : null);
                      const studentName = bookingStudent
                        ? `${bookingStudent.firstName} ${bookingStudent.lastName}`
                        : 'Student';
                      const schedule = lesson
                        ? formatLessonSchedule(lesson, booking)
                        : null;
                      const enrollmentScopeLabel = getBookingEnrollmentScopeLabel({
                        sessionDate: booking.sessionDate,
                        lessonType: lesson?.lessonType,
                      });
                      return (
                        <div
                          key={booking.id}
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1 flex-wrap gap-y-1">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {lesson?.name || 'Lesson'}
                              </h4>
                              {isMultiSport && lesson && resolveAcademySport(academySports, lesson.academySportId) ? (
                                <SportBadge sport={resolveAcademySport(academySports, lesson.academySportId)!} />
                              ) : null}
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300">
                                {studentName}
                              </span>
                              {enrollmentScopeLabel && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                  {enrollmentScopeLabel}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-0.5">
                              {schedule && <p>{schedule}</p>}
                              {term && (
                                <p className="text-gray-500 dark:text-gray-500">{term.name}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-2 flex-shrink-0"
                            onClick={() => navigate(paths?.calendar ?? paths?.bookings ?? '/org-picker')}
                          >
                            Details
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Your Academy
            </h3>
            {displayAcademy ? (
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 dark:text-primary-400 text-2xl font-bold">{displayAcademy.name.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{displayAcademy.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{displayAcademy.address}</p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Button variant="outline" size="sm" onClick={() => navigate(paths?.base ?? '/org-picker')}>
                          Visit Academy
                        </Button>
                        <Button size="sm" onClick={() => navigate(paths?.lessons ?? '/org-picker')}>
                          Browse Lessons
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-gray-600">Not enrolled in an academy yet.</p>
                  <Button className="mt-4" onClick={() => navigate(paths?.lessons ?? '/org-picker')}>Browse Lessons</Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* My Waitlist */}
        {waitlistLoading ? (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">On Waitlist</h3>
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        ) : waitlistEntries.length > 0 ? (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              On Waitlist
            </h3>
            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {waitlistEntries.map((entry) => {
                    const lesson = lessons.find((l) => l.id === entry.lessonId);
                    const guardianName = [entry.customerFirstName, entry.customerLastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim();
                    const studentDisplay = [entry.studentFirstName, entry.studentLastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim();
                    const queuedFor = studentDisplay || guardianName || 'Student';
                    return (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {lesson?.name || 'Lesson'}
                          </h4>
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mt-0.5 truncate">
                            {queuedFor}
                          </p>
                          {entry.customerEmail ? (
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {entry.customerEmail}
                            </p>
                          ) : null}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-2 flex-shrink-0"
                          onClick={() => handleLeaveWaitlist(entry.id)}
                          disabled={leavingEntryId === entry.id}
                        >
                          {leavingEntryId === entry.id ? 'Leaving...' : 'Leave'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}

        </>
        ) : null}

        <TourAlertDialog
          isOpen={showTourDialog && !isTourCompleted && hasLessonsAccess}
          setIsOpen={setShowTourDialog}
          title={`Welcome to ${academyName}!`}
          description={`Discover how to get the most out of your ${academyName} dashboard. We'll show you the essentials in just a few steps.`}
        />
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuthStore();

  const isTourCompletedInDb = user?.preferences?.tourCompletedStudentDashboard ?? false;

  const handleTourComplete = async () => {
    try {
      await userService.updatePreferences({
        tourCompletedStudentDashboard: true,
      });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: { ...cur.preferences, tourCompletedStudentDashboard: true },
          },
        });
      }
    } catch (error) {
      console.error('Failed to persist tour completion:', error);
    }
  };

  return (
    <TourProvider
      isTourCompleted={isTourCompletedInDb}
      onComplete={handleTourComplete}
    >
      <StudentDashboardContent />
    </TourProvider>
  );
}
